
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { z } from "zod";
import { toast } from "sonner";
import { isValidInput, validateUserInput } from "@/utils/security";

interface InviteTeamMemberFormProps {
  onInvite: (data: { email: string; role: "admin" | "member" }) => void;
  isInviting: boolean;
}

export function InviteTeamMemberForm({ onInvite, isInviting }: InviteTeamMemberFormProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [error, setError] = useState("");

  const validateEmail = (email: string) => {
    try {
      // First check if email format is valid
      z.string().email().parse(email);
      
      // Then check if it contains any dangerous input
      const validation = validateUserInput(email);
      if (!validation.isValid) {
        if (validation.message) {
          toast.error(validation.message);
        }
        return false;
      }
      
      if (!isValidInput(email)) {
        toast.error("Please enter a valid email without HTML tags or scripts");
        return false;
      }
      
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear previous errors
    setError("");
    
    // For immediate feedback on obviously malicious input
    if (newEmail && !validateEmail(newEmail)) {
      // Only show error in the form, toast will be shown by validateEmail
      setError("Please enter a valid email address");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Show a toast when the invitation is sent
    toast.info(`Sending invitation to ${email}...`);
    
    onInvite({ email, role });
    setEmail("");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          type="email"
          placeholder="colleague@example.com"
          value={email}
          onChange={handleEmailChange}
          required
          className={error ? "border-red-500" : ""}
        />
        {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
      </div>

      <div className="space-y-2">
        <Label>Role</Label>
        <RadioGroup
          value={role}
          onValueChange={(value) => setRole(value as "admin" | "member")}
          className="mt-2"
        >
          <div className="flex items-center space-x-2 mb-2">
            <RadioGroupItem value="admin" id="admin" />
            <Label htmlFor="admin" className="cursor-pointer">
              <span className="font-medium">Admin</span>
              <p className="text-sm text-muted-foreground">
                Can manage team members and all account settings
              </p>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="member" id="member" />
            <Label htmlFor="member" className="cursor-pointer">
              <span className="font-medium">Member</span>
              <p className="text-sm text-muted-foreground">
                Can use the application but cannot manage team or billing
              </p>
            </Label>
          </div>
        </RadioGroup>
      </div>

      <Button
        type="submit"
        className="w-full bg-black hover:bg-black/90"
        disabled={isInviting || !email}
      >
        {isInviting ? "Sending Invitation..." : "Send Invitation"}
      </Button>
      
      <p className="text-xs text-gray-500 mt-4">
        If the invitation email is not received, please check the spam folder or try again.
      </p>
    </form>
  );
}
